import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { HardDrives, Plugs, Cpu, Copy, DownloadSimple, CircleNotch, CheckCircle } from "@phosphor-icons/react";

const OLLAMA_SYS =
  "You are an expert AutoLISP / Visual LISP programmer for AutoCAD, AutoCAD Civil 3D and progeCAD. " +
  "Write complete, robust, load-clean routines: balanced parentheses, terminated strings, locals declared " +
  "after '/', error handlers that restore sysvars, and a trailing (princ) on every c: command. " +
  "Return ONLY the .lsp code — no prose, no markdown fences.";

const stripFences = (t) => (t || "").replace(/```[a-z]*\n?/gi, "").replace(/```/g, "").trim();

const MODELS = ["qwen2.5-coder", "qwen2.5-coder:14b", "deepseek-coder-v2", "codellama", "llama3.1"];

export const LocalAI = ({ clientId, clientName, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState(() => localStorage.getItem("lf_ollama_url") || "http://localhost:11434");
  const [model, setModel] = useState(() => localStorage.getItem("lf_ollama_model") || "qwen2.5-coder");
  const [prompt, setPrompt] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [connOk, setConnOk] = useState(null);
  const [ground, setGround] = useState({ grounding: "", has_profile: false, counts: {} });
  const [useGround, setUseGround] = useState(true);

  useEffect(() => {
    if (!open || !clientId) { setGround({ grounding: "", has_profile: false, counts: {} }); return; }
    api.grounding(clientId).then(setGround).catch(() => setGround({ grounding: "", has_profile: false, counts: {} }));
  }, [open, clientId]);

  const save = () => { localStorage.setItem("lf_ollama_url", url); localStorage.setItem("lf_ollama_model", model); };

  const testConn = async () => {
    setConnOk(null);
    try {
      const r = await fetch(`${url.replace(/\/$/, "")}/api/tags`);
      if (!r.ok) throw new Error();
      const d = await r.json();
      setConnOk(true);
      const names = (d.models || []).map((m) => m.name).join(", ");
      toast.success(names ? `Connected. Models: ${names}` : "Connected to Ollama.");
      save();
    } catch {
      setConnOk(false);
      toast.error("Could not reach Ollama. Is it running, and is OLLAMA_ORIGINS set? (see the Setup tab)");
    }
  };

  const generate = async () => {
    if (!prompt.trim()) { toast.error("Describe the routine you want."); return; }
    setBusy(true); setCode("");
    save();
    const grounded = useGround && ground.has_profile && ground.grounding;
    const fullPrompt = grounded
      ? `${ground.grounding}\n\n---\nTASK: ${prompt}`
      : prompt;
    try {
      const r = await fetch(`${url.replace(/\/$/, "")}/api/generate`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ model, system: OLLAMA_SYS, prompt: fullPrompt, stream: false }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setCode(stripFences(d.response));
      toast.success(grounded ? `Generated locally, grounded in ${clientName}'s profile.` : "Generated locally with Ollama.");
    } catch (e) {
      toast.error(`Local generation failed: ${e.message}. Check the model is pulled (ollama pull ${model}).`);
    } finally { setBusy(false); }
  };

  const copy = () => { navigator.clipboard.writeText(code); toast.success("Copied"); };
  const download = () => {
    const blob = new Blob([code], { type: "text/plain" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = "local_ollama.lsp"; a.click(); URL.revokeObjectURL(a.href);
  };

  const validateRefine = async () => {
    if (!code.trim()) return;
    setBusy(true);
    const t = toast.loading("Linting & auto-refining…");
    try {
      const r = await api.validateLisp({ code, platform: "AutoCAD", version: "", refine: true });
      setCode(r.code);
      const v = r.validation;
      if (v?.ok) toast.success(`Clean — refined in ${v.refine_passes || 0} pass(es).`, { id: t });
      else toast.warning(`${(v?.errors || []).length} issue(s) remain after refine.`, { id: t });
    } catch { toast.error("Validation failed.", { id: t }); }
    finally { setBusy(false); }
  };

  const sendToWorkspace = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      await api.importGeneration({ client_id: clientId || null, prompt: `[Local AI] ${prompt.slice(0, 120)}`, code, platform: "AutoCAD", version: "" });
      toast.success(clientId ? `Saved to ${clientName || "client"} history.` : "Saved to history.");
      onSaved?.();
    } catch { toast.error("Could not save to workspace."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="local-ai-btn" className="flex items-center gap-1.5 text-xs font-mono text-[var(--text-dim)] hover:text-[var(--primary)]">
          <HardDrives size={15} weight="bold" /> Local AI
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)] max-w-2xl rounded-[4px] max-h-[90vh] overflow-y-auto" data-testid="local-ai-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono text-base flex items-center gap-2"><HardDrives size={18} weight="bold" className="text-[var(--primary)]" />Local AI · Open-Source Models (Ollama)</DialogTitle>
          <DialogDescription className="text-[var(--text-dim)] text-xs">
            Generate on your own machine with open-source coder models (Qwen2.5-Coder, DeepSeek-Coder, Llama) — free, private, no API keys. Runs entirely in your browser against your local Ollama.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="gen">
          <TabsList className="bg-[var(--bg)] border border-[var(--border)] rounded-[4px]">
            <TabsTrigger value="gen" data-testid="local-ai-tab-gen" className="text-xs font-mono data-[state=active]:bg-[var(--primary)] data-[state=active]:text-white">Generate</TabsTrigger>
            <TabsTrigger value="setup" data-testid="local-ai-tab-setup" className="text-xs font-mono data-[state=active]:bg-[var(--primary)] data-[state=active]:text-white">Setup guide</TabsTrigger>
          </TabsList>

          <TabsContent value="gen" className="flex flex-col gap-3 pt-3">
            <div className="grid grid-cols-[1fr_auto] gap-2">
              <Input value={url} onChange={(e) => setUrl(e.target.value)} data-testid="ollama-url"
                placeholder="http://localhost:11434" className="h-8 rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-[11px]" />
              <Button size="sm" variant="outline" onClick={testConn} data-testid="ollama-test"
                className="h-8 rounded-[3px] text-xs gap-1.5 border-[var(--border)] bg-transparent">
                {connOk === true ? <CheckCircle size={13} className="text-[var(--success)]" /> : <Plugs size={13} />} Test
              </Button>
            </div>
            <Input list="ollama-models" value={model} onChange={(e) => setModel(e.target.value)} data-testid="ollama-model"
              placeholder="qwen2.5-coder" className="h-8 rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-[11px]" />
            <datalist id="ollama-models">{MODELS.map((m) => <option key={m} value={m} />)}</datalist>
            {clientId && (
              <label className="flex items-center gap-2 text-[11px] font-mono cursor-pointer" data-testid="ollama-ground-label">
                <input type="checkbox" checked={useGround} disabled={!ground.has_profile}
                  onChange={(e) => setUseGround(e.target.checked)} data-testid="ollama-ground" className="accent-[var(--primary)]" />
                <span className={ground.has_profile ? "text-[var(--text)]" : "text-[var(--text-dim)]"}>
                  {ground.has_profile
                    ? `Match ${clientName || "client"}'s CAD profile (${ground.counts?.layers || 0} layers, ${ground.counts?.commands || 0} commands)`
                    : `No CAD profile for ${clientName || "this client"} yet — attach a finished DWG/DXF to ground local generation`}
                </span>
              </label>
            )}
            <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid="ollama-prompt"
              placeholder="Describe the routine — e.g. Draw a rectangle from two picked points on layer WALLS and label its area."
              className="min-h-[90px] rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-xs" />
            <Button onClick={generate} disabled={busy} data-testid="ollama-generate"
              className="h-9 rounded-[3px] gap-2 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white">
              {busy ? <CircleNotch size={15} className="animate-spin" /> : <Cpu size={15} weight="bold" />} {busy ? "Generating locally…" : "Generate locally"}
            </Button>
            {code && (
              <div className="border border-[var(--border)] rounded-[3px]" data-testid="ollama-result">
                <div className="flex items-center gap-2 px-2 py-1 border-b border-[var(--border)] bg-[var(--surface-2)]">
                  <button onClick={validateRefine} disabled={busy} data-testid="ollama-validate" className="text-[11px] font-mono text-[var(--primary)] hover:underline flex items-center gap-1 disabled:opacity-40"><CheckCircle size={12} /> Validate &amp; refine</button>
                  <button onClick={sendToWorkspace} disabled={busy} data-testid="ollama-send" className="text-[11px] font-mono text-[var(--primary)] hover:underline flex items-center gap-1 disabled:opacity-40"><DownloadSimple size={12} /> Send to workspace</button>
                  <div className="ml-auto flex items-center gap-2">
                    <button onClick={copy} data-testid="ollama-copy" className="text-[11px] font-mono text-[var(--text-dim)] hover:text-[var(--primary)] flex items-center gap-1"><Copy size={12} /> Copy</button>
                    <button onClick={download} data-testid="ollama-download" className="text-[11px] font-mono text-[var(--text-dim)] hover:text-[var(--primary)] flex items-center gap-1"><DownloadSimple size={12} /> .lsp</button>
                  </div>
                </div>
                <pre className="p-3 text-[11px] font-mono whitespace-pre-wrap overflow-x-auto max-h-[300px]">{code}</pre>
              </div>
            )}
          </TabsContent>

          <TabsContent value="setup" className="flex flex-col gap-3 pt-3 text-[12px] leading-relaxed" data-testid="local-ai-setup">
            <div>
              <div className="font-mono text-[var(--primary)] text-[13px] mb-1">1 · Install Ollama (free, open-source)</div>
              <p className="text-[var(--text-dim)]">Download from <b>ollama.com</b> (macOS / Windows / Linux). It runs a local server at <code>http://localhost:11434</code>.</p>
            </div>
            <div>
              <div className="font-mono text-[var(--primary)] text-[13px] mb-1">2 · Pull an open-source coder model</div>
              <pre className="bg-[var(--bg)] border border-[var(--border)] rounded-[3px] p-2 text-[11px] font-mono">ollama pull qwen2.5-coder{"\n"}ollama pull deepseek-coder-v2{"\n"}ollama pull llama3.1</pre>
              <p className="text-[var(--text-dim)] mt-1">Qwen2.5-Coder and DeepSeek-Coder-V2 are the strongest open models for LISP/code.</p>
            </div>
            <div>
              <div className="font-mono text-[var(--primary)] text-[13px] mb-1">3 · Allow this app to talk to Ollama (CORS)</div>
              <p className="text-[var(--text-dim)]">Start Ollama with your origin allowed so the browser can reach it:</p>
              <pre className="bg-[var(--bg)] border border-[var(--border)] rounded-[3px] p-2 text-[11px] font-mono">OLLAMA_ORIGINS=* ollama serve</pre>
              <p className="text-[var(--text-dim)] mt-1">Then hit <b>Test</b> on the Generate tab.</p>
            </div>
            <div>
              <div className="font-mono text-[var(--primary)] text-[13px] mb-1">4 · Edit &amp; test the .lsp (optional, recommended)</div>
              <p className="text-[var(--text-dim)]">Install <b>VS Code</b> + Autodesk's open-source <b>AutoCAD AutoLISP Extension</b> to edit/debug generated <code>.lsp</code>. For in-editor AI, add <b>Continue.dev</b> and point it at your local Ollama — a fully-offline, no-key coding loop.</p>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
